<template>
  <div class="p-6">
    <div class="mb-6 flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Pedidos</h1>
        <p class="text-gray-600 dark:text-gray-400">Listado de pedidos POS2</p>
      </div>
      <router-link
        to="/pos2/pedidos/nuevo"
        class="px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition flex items-center gap-1"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
        Nuevo Pedido
      </router-link>
    </div>

    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <!-- Filtros -->
      <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-2 items-center">
        <input
          v-model="filtros.buscar"
          @keyup.enter="buscar"
          type="text"
          placeholder="Buscar por folio o cliente..."
          class="px-3 py-2 rounded border text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200 w-72"
        />
        <button @click="buscar" class="px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition">Buscar</button>
        <button @click="limpiar" class="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition">Limpiar</button>
      </div>

      <!-- Tabla -->
      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left">
          <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 text-blue-600 dark:text-blue-400 border-b-2 border-gray-200 dark:border-gray-700">
            <tr>
              <th class="px-4 py-2">Folio</th>
              <th class="px-4 py-2">Cliente</th>
              <th class="px-4 py-2">Fecha Alta</th>
              <th class="px-4 py-2">Fecha Entrega</th>
              <th class="px-4 py-2 text-right">Total Venta</th>
              <th class="px-4 py-2">Estado</th>
              <th class="px-4 py-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="7" class="px-4 py-8 text-center text-gray-400">Cargando...</td>
            </tr>
            <template v-else>
              <tr
                v-for="pedido in pedidos"
                :key="pedido.id"
                class="border-b dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <td class="px-4 py-2 font-mono text-violet-700 dark:text-violet-400 font-semibold">{{ pedido.folio ?? '—' }}</td>
                <td class="px-4 py-2 font-medium text-gray-900 dark:text-white">{{ pedido.cliente?.nombre }}</td>
                <td class="px-4 py-2 text-gray-600 dark:text-gray-300">{{ formatDate(pedido.fecha_alta) }}</td>
                <td class="px-4 py-2 text-gray-600 dark:text-gray-300">{{ pedido.fecha_entrega ? formatDate(pedido.fecha_entrega) : '—' }}</td>
                <td class="px-4 py-2 text-right font-medium text-gray-900 dark:text-white">{{ formatMoney(pedido.total_venta) }}</td>
                <td class="px-4 py-2">
                  <span :class="estadoClass(pedido.estado)" class="px-2 py-0.5 rounded-full text-xs font-semibold">
                    {{ estadoLabel(pedido.estado) }}
                  </span>
                </td>
                <td class="px-4 py-2">
                  <button @click="verPedido(pedido)" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 p-1" title="Ver/Editar">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                  </button>
                </td>
              </tr>

              <tr v-if="pedidos.length === 0">
                <td colspan="7" class="px-4 py-8 text-center text-gray-400">No se encontraron pedidos</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="pagination.total > 0" class="flex justify-between items-center px-4 py-3 border-t border-gray-200 dark:border-gray-700">
        <div class="text-sm text-gray-600 dark:text-gray-400">
          Mostrando {{ ((pagination.current_page - 1) * pagination.per_page) + 1 }}
          a {{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }}
          de {{ pagination.total }} registros
        </div>
        <div class="flex gap-2">
          <button @click="cambiarPagina(pagination.current_page - 1)" :disabled="pagination.current_page <= 1" class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-sm disabled:opacity-50">Anterior</button>
          <span class="px-3 py-1 text-sm text-gray-600 dark:text-gray-400">Página {{ pagination.current_page }} de {{ pagination.last_page }}</span>
          <button @click="cambiarPagina(pagination.current_page + 1)" :disabled="pagination.current_page >= pagination.last_page" class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-sm disabled:opacity-50">Siguiente</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useHttp } from '@/composables/useHttp.js'

const router = useRouter()
const { get, data } = useHttp()

const pedidos    = ref([])
const loading    = ref(false)
const filtros    = ref({ buscar: '' })
const pagination = ref({ current_page: 1, last_page: 1, per_page: 15, total: 0 })

const cargar = async (pagina = 1) => {
  try {
    loading.value = true
    const params = new URLSearchParams({ page: pagina, per_page: pagination.value.per_page })
    if (filtros.value.buscar) params.append('buscar', filtros.value.buscar)
    await get(`/pos2/pedidos?${params}`)
    pedidos.value = data.value.data
    pagination.value = {
      current_page: data.value.current_page,
      last_page:    data.value.last_page,
      per_page:     data.value.per_page,
      total:        data.value.total,
    }
  } finally {
    loading.value = false
  }
}

const buscar        = () => cargar(1)
const limpiar       = () => { filtros.value.buscar = ''; cargar(1) }
const cambiarPagina = (p) => { if (p >= 1 && p <= pagination.value.last_page) cargar(p) }

const verPedido = (pedido) => router.push(`/pos2/pedidos/${pedido.id}/editar`)

const formatDate = (val) => {
  if (!val) return '—'
  return new Date(val).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const formatMoney = (val) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val ?? 0)

const estadoLabel = (estado) => ({
  borrador:   'Borrador',
  guardado:   'Guardado',
  cotizacion: 'Cotización',
}[estado] ?? estado)

const estadoClass = (estado) => ({
  borrador:   'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  guardado:   'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  cotizacion: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
}[estado] ?? '')

onMounted(() => cargar())
</script>
